import { useState } from 'react';
import { X, RefreshCw, Trash2, ChevronDown, ChevronRight, Loader2, Clock, Play, AlertTriangle } from 'lucide-react';
import type { QueueJob } from './types';

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatTs(ts: number | undefined | null): string {
  if (!ts) return '--';
  return new Date(ts).toLocaleString();
}

interface Props {
  job: QueueJob;
  queueName: string;
  acting: string | null;
  onRetry: (jobId: string, queueName: string) => void;
  onRemove: (jobId: string, queueName: string) => void;
  onClose: () => void;
}

export function JobDetailModal({ job, queueName, acting, onRetry, onRemove, onClose }: Props) {
  const [showPayload, setShowPayload] = useState(false);
  const [expandedTrace, setExpandedTrace] = useState<number | null>(null);

  const attempts = job.attemptsMade ?? 0;
  const maxAttempts = job.maxAttempts ?? 1;
  const exhausted = attempts >= maxAttempts;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{job.name}</h3>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono mt-0.5">{job.id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Attempts + Timing */}
          <div className="grid grid-cols-4 gap-2">
            <div className={`rounded-lg p-2 text-center ${exhausted ? 'bg-red-50' : 'bg-amber-50'}`}>
              <div className={`text-sm font-semibold ${exhausted ? 'text-red-700' : 'text-amber-700'}`}>
                {attempts}/{maxAttempts}
              </div>
              <div className={`text-[9px] ${exhausted ? 'text-red-500' : 'text-amber-500'}`}>
                {exhausted ? 'Exhausted' : 'Attempts'}
              </div>
            </div>
            <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-gray-950">
              <div className="text-[10px] font-medium text-gray-700 dark:text-gray-200">{formatTs(job.timestamp)}</div>
              <div className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center justify-center gap-0.5">
                <Clock className="w-2.5 h-2.5" /> Created
              </div>
            </div>
            <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-gray-950">
              <div className="text-[10px] font-medium text-gray-700 dark:text-gray-200">{formatTs(job.processedOn)}</div>
              <div className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center justify-center gap-0.5">
                <Play className="w-2.5 h-2.5" /> Started
              </div>
            </div>
            <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-gray-950">
              <div className="text-[10px] font-medium text-gray-700 dark:text-gray-200">
                {job.finishedOn ? timeAgo(job.finishedOn) : '--'}
              </div>
              <div className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center justify-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" /> Failed
              </div>
            </div>
          </div>

          {/* Error Message */}
          {job.failedReason && (
            <div>
              <div className="text-[10px] font-medium text-red-600 dark:text-red-400 mb-1">Error</div>
              <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-3 text-xs text-red-800 dark:text-red-400 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                {job.failedReason}
              </div>
            </div>
          )}

          {/* Stack Traces */}
          {job.stacktrace && job.stacktrace.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1">Stack Traces</div>
              <div className="space-y-1">
                {job.stacktrace.map((trace, i) => (
                  <div key={i} className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedTrace(expandedTrace === i ? null : i)}
                      className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950"
                    >
                      {expandedTrace === i ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Attempt {i + 1}
                    </button>
                    {expandedTrace === i && (
                      <pre className="font-mono text-[10px] bg-gray-900 dark:bg-gray-950 text-gray-100 p-3 overflow-x-auto max-h-48 overflow-y-auto">
                        {trace}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Job Payload */}
          <div>
            <button
              onClick={() => setShowPayload(!showPayload)}
              className="flex items-center gap-1.5 text-[10px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            >
              {showPayload ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Job Payload
            </button>
            {showPayload && (
              <pre className="mt-1 font-mono text-[10px] bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-3 overflow-x-auto max-h-48 overflow-y-auto text-gray-700 dark:text-gray-200">
                {JSON.stringify(job.data, null, 2)}
              </pre>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
          <button
            onClick={() => onRemove(job.id, queueName)}
            disabled={acting === `remove-${job.id}`}
            className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-white dark:hover:bg-gray-900 disabled:opacity-50 flex items-center gap-1.5"
          >
            {acting === `remove-${job.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Remove
          </button>
          <button
            onClick={() => onRetry(job.id, queueName)}
            disabled={acting === `retry-${job.id}`}
            className="px-3 py-1.5 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5"
          >
            {acting === `retry-${job.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}
